import { useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import type AvailableTime from '../../model/AvailableTime';
import type { SubmissionFailure } from './submission-state';
import type { AvailabilityLoadFailure } from './use-specialist-availabilities';
import {
  browserTimezone,
  buildSlotsLoadedParams,
  buildStepSnapshotParams,
  trackSlotsLoaded,
  trackSlotsLoadFailed,
  trackStepExit,
} from '../utils/booking-analytics';
import type {
  BookingStage, ExitTrigger, FeeStatus, SoonestSlot,
} from '../utils/booking-analytics';

interface PendingLoad {
  key: string
  month: string
  startedAt: number
  failureReported: boolean
}

interface SlotsLoadedTrackingInput {
  clinicianId: string
  clinicianName: string
  month: string
  days: AvailableTime[] | undefined
  appointmentLengthMinutes: number
  /** The clinician's soonest slot; 'loading' until their lookahead has finished. */
  nextAvailable: SoonestSlot
  lastLoadFailure: AvailabilityLoadFailure | null
}

/**
 * Reports booking_slots_loaded once per load the patient caused: entering the
 * step with a clinician chosen, or switching clinician or month. Background
 * refreshes keep the same clinician and month, so they never report again.
 * It waits for the clinician's name and their lookahead, so a saved
 * clinician whose month arrives first is not reported nameless or as fully
 * booked. A failed load reports booking_slots_load_failed once the name is
 * known, and the load still reports when a later retry brings the slots.
 */
export const useSlotsLoadedTracking = ({
  clinicianId, clinicianName, month, days, appointmentLengthMinutes, nextAvailable, lastLoadFailure,
}: SlotsLoadedTrackingInput): void => {
  const pendingRef = useRef<PendingLoad | null>(null);
  // Read when the event fires, so a name or soonest slot that settles later
  // does not count as a new load.
  const contextRef = useRef({ clinicianName, nextAvailable });
  contextRef.current = { clinicianName, nextAvailable };
  const key = clinicianId ? `${clinicianId}-${month}` : '';
  const hasName = clinicianName !== '';
  const ready = hasName && nextAvailable !== 'loading';

  useEffect(() => {
    pendingRef.current = key
      ? {
        key, month, startedAt: Date.now(), failureReported: false,
      }
      : null;
    // month is part of key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || pending.key !== key || days === undefined || !ready) return;
    pendingRef.current = null;
    trackSlotsLoaded(buildSlotsLoadedParams({
      clinicianName: contextRef.current.clinicianName,
      month: pending.month,
      nextAvailable: contextRef.current.nextAvailable,
      days,
      appointmentLengthMinutes,
      now: moment(),
      timezone: browserTimezone(),
    }));
  }, [key, days, appointmentLengthMinutes, ready]);

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || !lastLoadFailure || pending.failureReported || !hasName) return;
    if (`${lastLoadFailure.specialistId}-${lastLoadFailure.month}` !== pending.key) return;
    if (lastLoadFailure.failedAt < pending.startedAt) return;
    pendingRef.current = { ...pending, failureReported: true };
    trackSlotsLoadFailed(contextRef.current.clinicianName, pending.month);
  }, [lastLoadFailure, hasName]);
};

export interface StepSnapshot {
  stage: BookingStage
  failure: SubmissionFailure | null
  feeStatus: FeeStatus
  month: string
  /** Empty until a clinician is chosen. */
  clinicianName: string
  /** The chosen clinician's soonest slot. */
  nextAvailable: SoonestSlot
}

/**
 * Keeps a snapshot of where the patient is on the booking step and reports
 * booking_step_exit when they leave before a booking is confirmed:
 * - hidden: the page turned hidden. Mobile browsers rarely fire pagehide when
 *   the patient switches app or the tab is killed, so this is the main signal;
 *   it reports at most once until the page is visible again.
 * - pagehide: browser back, closing the tab or refresh.
 * - back: the step closed inside the app, i.e. either Back button.
 * Leaving while the confirmation is still pending is reported with stage
 * submitting, since that request may never succeed. Leaving after the booking
 * succeeds is not reported: bookingConfirmedRef turns true before the step
 * re-renders or closes. Sending while the page unloads is best effort.
 */
export const useStepExitTracking = (
  snapshot: StepSnapshot,
  bookingConfirmedRef: { readonly current: boolean },
): void => {
  const mountedAtRef = useRef(Date.now());
  const firstMonthRef = useRef(snapshot.month);
  const browsedOtherMonthsRef = useRef(false);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  const { month } = snapshot;
  useEffect(() => {
    if (month !== firstMonthRef.current) browsedOtherMonthsRef.current = true;
  }, [month]);

  useEffect(() => {
    let reported = false;
    const report = (trigger: ExitTrigger) => {
      const { current } = snapshotRef;
      if (reported || bookingConfirmedRef.current) return;
      reported = true;
      trackStepExit(buildStepSnapshotParams({
        stage: current.stage,
        failure: current.failure,
        feeStatus: current.feeStatus,
        browsedOtherMonths: browsedOtherMonthsRef.current,
        msOnStep: Date.now() - mountedAtRef.current,
        clinicianName: current.clinicianName,
        nextAvailable: current.nextAvailable,
        timezone: browserTimezone(),
      }), trigger);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        report('hidden');
      } else {
        reported = false;
      }
    };
    const handlePageHide = () => report('pagehide');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      // Skipped when hidden or pagehide already reported this departure.
      report('back');
    };
    // The ref object is stable for the step's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
